import type { AnalyticsWindowSnakeFields } from "./analyticsWindowParams";
import type { SimulateCandleBudgetSuggestedPeriod } from "../types/tradePolicy";

/** Report analysis OHLC cap (`MARKET_CONTEXT_MAX_BARS` on backend). */
export const REPORT_ANALYSIS_MAX_BARS = 60_000;

/** Execution chart OHLC cap (`EXECUTION_CANDLES_MAX_BARS` on backend). */
export const EXECUTION_CHART_MAX_BARS = 60_000;

/** Simulate OHLC cap — same analysis budget as report market context. */
export const SIMULATE_ANALYSIS_MAX_BARS = REPORT_ANALYSIS_MAX_BARS;

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

function inclusiveCalendarDaysBetweenYmd(startYmd: string, endYmd: string): number {
  const toUtcMs = (ymd: string) => {
    const [y, m, d] = ymd.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };
  const diffDays = (toUtcMs(endYmd) - toUtcMs(startYmd)) / 86400000;
  return Math.max(1, Math.floor(diffDays) + 1);
}

/** Add calendar days in UTC date arithmetic (YMD only; good enough for budget chunking). */
function addCalendarDaysYmdUtc(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + deltaDays));
  const yy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(next.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

const MINUTES_PER_CANDLE: Record<string, number> = {
  "1m": 1,
  "5m": 5,
  "15m": 15,
  "30m": 30,
  "1h": 60,
  "4h": 240,
  "1d": 1440,
};

/** Finest → coarsest (matches BE ladder including 1d). */
export const CANDLE_TIMEFRAME_ORDER = [
  "1m",
  "5m",
  "15m",
  "30m",
  "1h",
  "4h",
  "1d",
] as const;

export type CandleTimeframeId = (typeof CANDLE_TIMEFRAME_ORDER)[number];

const REPORT_TIMEFRAME_ORDER = CANDLE_TIMEFRAME_ORDER;

export function reportTimeframeRank(timeframe: string): number {
  return REPORT_TIMEFRAME_ORDER.indexOf(timeframe as (typeof REPORT_TIMEFRAME_ORDER)[number]);
}

/** True when `a` is a strictly coarser (larger bar) timeframe than `b`. */
export function isReportTimeframeCoarserThan(a: string, b: string): boolean {
  const ra = reportTimeframeRank(a);
  const rb = reportTimeframeRank(b);
  if (ra < 0 || rb < 0) return false;
  return ra > rb;
}

/**
 * Finest (smallest bar) among known candle TFs. Unknown values are ignored.
 * Returns `fallback` (default `1h`) when none are recognized.
 */
export function resolveFinestCandleTimeframe(
  timeframes: Array<string | null | undefined>,
  fallback: CandleTimeframeId = "1h"
): CandleTimeframeId {
  let best: CandleTimeframeId | null = null;
  let bestRank = Number.POSITIVE_INFINITY;
  for (const raw of timeframes) {
    const tf = String(raw ?? "").trim().toLowerCase();
    if (!tf) continue;
    const rank = reportTimeframeRank(tf);
    if (rank < 0) continue;
    if (rank < bestRank) {
      bestRank = rank;
      best = tf as CandleTimeframeId;
    }
  }
  return best ?? fallback;
}

/** Normalize report/policy candle TF strings to a known ladder value. */
export function normalizeCandleTimeframe(
  raw: string | null | undefined,
  fallback: CandleTimeframeId = "1h"
): CandleTimeframeId {
  const tf = String(raw ?? "").trim().toLowerCase();
  if (reportTimeframeRank(tf) >= 0) return tf as CandleTimeframeId;
  return fallback;
}

/** Calendar days implied by preset timeframe when explicit local/ISO dates are absent. */
const PRESET_TIMEFRAME_DAYS: Record<string, number> = {
  today: 1,
  "7-days": 7,
  "14-days": 14,
  "30-days": 30,
  "all-time": 365,
};

function ymdUtcFromDate(d: Date): string {
  const yy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * Concrete YMD bounds for budget chunking. Presets (including all-time ≈ 365 days)
 * resolve to a rolling lookback ending on `asOf` so period chips can render without
 * an explicit custom date range.
 */
export function resolveBudgetWindowYmdBounds(
  windowFields: AnalyticsWindowSnakeFields,
  asOf: Date = new Date()
): { start: string; end: string } | null {
  if (windowFields.local_start_date && windowFields.local_end_date) {
    const start = windowFields.local_start_date.slice(0, 10);
    const end = windowFields.local_end_date.slice(0, 10);
    if (YMD_RE.test(start) && YMD_RE.test(end) && start <= end) return { start, end };
  }
  if (windowFields.start_date && windowFields.end_date) {
    const start = windowFields.start_date.slice(0, 10);
    const end = windowFields.end_date.slice(0, 10);
    if (YMD_RE.test(start) && YMD_RE.test(end) && start <= end) return { start, end };
  }
  const tf = windowFields.timeframe?.trim();
  const days = tf ? PRESET_TIMEFRAME_DAYS[tf] : undefined;
  if (!days || days <= 0) return null;
  const end = ymdUtcFromDate(asOf);
  const start = addCalendarDaysYmdUtc(end, -(days - 1));
  return { start, end };
}

export type CandleWindowEstimate = {
  dayCount: number;
  barCount: number;
  windowLabel: string;
};

export function estimateCandleWindowForBarBudget(
  windowFields: AnalyticsWindowSnakeFields,
  windowSummaryLabel?: string
): CandleWindowEstimate | null {
  const tf = windowFields.timeframe?.trim();
  if (windowFields.local_start_date && windowFields.local_end_date) {
    const dayCount = inclusiveCalendarDaysBetweenYmd(
      windowFields.local_start_date,
      windowFields.local_end_date
    );
    return {
      dayCount,
      barCount: 0,
      windowLabel: windowSummaryLabel?.trim() || `${dayCount} days`,
    };
  }
  if (tf && PRESET_TIMEFRAME_DAYS[tf]) {
    const dayCount = PRESET_TIMEFRAME_DAYS[tf];
    const presetLabels: Record<string, string> = {
      today: "Today",
      "7-days": "Last 7 days",
      "14-days": "Last 14 days",
      "30-days": "Last 30 days",
      "all-time": "All time (~365 days)",
    };
    return {
      dayCount,
      barCount: 0,
      windowLabel: windowSummaryLabel?.trim() || presetLabels[tf] || tf,
    };
  }
  if (windowFields.start_date && windowFields.end_date) {
    const start = windowFields.start_date.slice(0, 10);
    const end = windowFields.end_date.slice(0, 10);
    if (YMD_RE.test(start) && YMD_RE.test(end)) {
      const dayCount = inclusiveCalendarDaysBetweenYmd(start, end);
      return {
        dayCount,
        barCount: 0,
        windowLabel: windowSummaryLabel?.trim() || `${dayCount} days`,
      };
    }
  }
  return null;
}

export function estimatedAnalysisBarCount(
  candleTimeframe: string,
  dayCount: number
): number | null {
  const minutes = MINUTES_PER_CANDLE[candleTimeframe];
  if (!minutes || dayCount <= 0) return null;
  const barsPerDay = (24 * 60) / minutes;
  return Math.ceil(dayCount * barsPerDay);
}

export function timeframeExceedsReportBarBudget(
  candleTimeframe: string,
  windowFields: AnalyticsWindowSnakeFields,
  windowSummaryLabel?: string
): { exceeds: boolean; estimate: CandleWindowEstimate | null; barCount: number | null } {
  const estimate = estimateCandleWindowForBarBudget(windowFields, windowSummaryLabel);
  if (!estimate) return { exceeds: false, estimate: null, barCount: null };
  const barCount = estimatedAnalysisBarCount(candleTimeframe, estimate.dayCount);
  if (barCount == null) return { exceeds: false, estimate, barCount: null };
  return {
    exceeds: barCount > REPORT_ANALYSIS_MAX_BARS,
    estimate: { ...estimate, barCount },
    barCount,
  };
}

/** Smallest standard TF that fits within the bar cap for the given day span. */
export function suggestedCoarserTimeframe(dayCount: number): string | null {
  if (dayCount <= 0) return null;
  for (const tf of CANDLE_TIMEFRAME_ORDER) {
    const bars = estimatedAnalysisBarCount(tf, dayCount);
    if (bars != null && bars <= REPORT_ANALYSIS_MAX_BARS) return tf;
  }
  return "1d";
}

/** Max inclusive calendar days that fit under `maxBars` at `candleTf` (naive 24h bars). */
export function maxCalendarDaysForBarBudget(
  candleTf: string,
  maxBars: number = SIMULATE_ANALYSIS_MAX_BARS
): number | null {
  const minutes = MINUTES_PER_CANDLE[candleTf];
  if (!minutes || maxBars <= 0) return null;
  const barsPerDay = (24 * 60) / minutes;
  return Math.max(1, Math.floor(maxBars / barsPerDay));
}

export type BuildSuggestedSimulatePeriodsInput = {
  desiredStart: string;
  desiredEnd: string;
  candleTf: string;
  maxBars?: number;
  /** Kept for API compatibility; chunking uses UTC calendar days. */
  timeZone?: string;
};

/**
 * Contiguous non-overlapping YMD slices covering the desired range (newest-first).
 * Client fallback when BE candle-budget-preview is unavailable.
 */
export function buildSuggestedSimulatePeriods(
  input: BuildSuggestedSimulatePeriodsInput
): SimulateCandleBudgetSuggestedPeriod[] {
  const maxBars = input.maxBars ?? SIMULATE_ANALYSIS_MAX_BARS;
  const maxDays = maxCalendarDaysForBarBudget(input.candleTf, maxBars);
  if (maxDays == null) return [];

  const start = input.desiredStart.slice(0, 10);
  const end = input.desiredEnd.slice(0, 10);
  if (!YMD_RE.test(start) || !YMD_RE.test(end) || start > end) return [];

  const totalDays = inclusiveCalendarDaysBetweenYmd(start, end);
  if (totalDays <= maxDays) {
    const barCount = estimatedAnalysisBarCount(input.candleTf, totalDays) ?? undefined;
    return [
      {
        window_start: start,
        window_end: end,
        bar_count: barCount,
        is_current: true,
        label: `${start} → ${end}`,
      },
    ];
  }

  const periods: SimulateCandleBudgetSuggestedPeriod[] = [];
  let chunkEnd = end;
  while (chunkEnd >= start) {
    const chunkStartCandidate = addCalendarDaysYmdUtc(chunkEnd, -(maxDays - 1));
    const chunkStart = chunkStartCandidate < start ? start : chunkStartCandidate;
    const dayCount = inclusiveCalendarDaysBetweenYmd(chunkStart, chunkEnd);
    periods.push({
      window_start: chunkStart,
      window_end: chunkEnd,
      bar_count: estimatedAnalysisBarCount(input.candleTf, dayCount) ?? undefined,
      is_current: periods.length === 0,
      label: `${chunkStart} → ${chunkEnd}`,
    });
    if (chunkStart <= start) break;
    chunkEnd = addCalendarDaysYmdUtc(chunkStart, -1);
  }
  return periods;
}

/** Collect candle TFs from a policy tree (bundle segments + dual-TF request fields). */
export function collectPolicyCandleTimeframes(policy: unknown): string[] {
  if (!policy || typeof policy !== "object" || Array.isArray(policy)) return [];
  const record = policy as Record<string, unknown>;
  const out: string[] = [];
  if (typeof record.candle_timeframe === "string" && record.candle_timeframe.trim()) {
    out.push(record.candle_timeframe.trim());
  }
  const segments = Array.isArray(record.segments) ? record.segments : [];
  for (const seg of segments) {
    if (!seg || typeof seg !== "object" || Array.isArray(seg)) continue;
    const tf = (seg as { candle_timeframe?: unknown }).candle_timeframe;
    if (typeof tf === "string" && tf.trim()) out.push(tf.trim());
  }
  return out;
}
